import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  DashboardCircleIcon,
  UserIcon,
  File01Icon,
  BankIcon,
  AccountSetting01Icon,
  ArrowLeft01Icon,
  GlobeIcon,
  Search01Icon,
  Notification01Icon,
  ArrowDown01Icon,
  Add01Icon,
  FileAddIcon,
  Activity01Icon,
  Camera01Icon,
  Logout01Icon,
  CheckmarkCircle02Icon,
} from '@hugeicons/core-free-icons';
import { SaaeLogo } from '@components/SaaeLogo';
import { usePermissions } from '@hooks/usePermissions';
import { BACKOFFICE_BASE, BACKOFFICE_LOGIN_PATH, backofficePath } from '@lib/backofficeRoutes';
import { NAV_PERMISSION_MAP } from '@lib/rbac';
import { useLanguage } from '@hooks/useLanguage';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import {
  getProfile,
  getAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  type AdminNotificationItem,
} from '@services/api';
import { apiClient } from '@services/apiClient';
import { springSidebar, pageTransition } from '@utils/motionConfig';
import { RtlDirectionalIcon } from '@utils/rtlIcons';
import { SIDEBAR_BREAKPOINT_PX } from '@layouts/BrokerageLayout';
import avatarDefault from '../public/images/avatardefault.png';

const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 72;

const ALL_NAV_ITEMS = [
  { to: BACKOFFICE_BASE, end: true, labelKey: 'admin.nav.overview', icon: DashboardCircleIcon },
  { to: backofficePath('brokers'), end: false, labelKey: 'admin.nav.brokers', icon: UserIcon },
  { to: backofficePath('brokerage'), end: false, labelKey: 'admin.nav.deals', icon: File01Icon },
  { to: backofficePath('commission-reports'), end: false, labelKey: 'admin.nav.reports', icon: BankIcon },
  { to: backofficePath('system-logs'), end: true, labelKey: 'admin.nav.systemLogs', icon: Activity01Icon },
  { to: backofficePath('settings'), end: false, labelKey: 'admin.nav.settings', icon: AccountSetting01Icon },
];

const NAV_LABEL_DEFAULTS: Record<string, string> = {
  'admin.nav.overview': 'Overview',
  'admin.nav.brokers': 'Brokers',
  'admin.nav.deals': 'Deals',
  'admin.nav.reports': 'Reports',
  'admin.nav.systemLogs': 'System Logs',
  'admin.nav.settings': 'Settings',
};

function useIsNarrow(breakpointPx: number) {
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const handler = () => setIsNarrow(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpointPx]);
  return isNarrow;
}

/** Shown when the avatar modal chunk fails to load. */
function AvatarUploadModalFallback({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="rounded-2xl bg-surface-cards p-6 shadow-lg max-w-sm text-center">
        <p className="text-primary font-sans mb-4">
          {t('profile.avatar.loadFailed', { defaultValue: 'Could not load avatar upload. Please refresh the page.' })}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[16px] bg-primary px-4 py-2 text-white font-medium"
        >
          {t('common.close', { defaultValue: 'Close' })}
        </button>
      </div>
    </div>
  );
}

const AvatarUploadModal = React.lazy(() =>
  import('@components/AvatarUploadModal/AvatarUploadModal')
    .then((m) => ({ default: m.AvatarUploadModal }))
    .catch((err) => {
      console.error('AvatarUploadModal load failed', err);
      return { default: AvatarUploadModalFallback };
    })
);

export function AdminLayout() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { can, isCustomerService: _isCustomerService } = usePermissions();
  const hasAnyWritePermission =
    can('CAN_CREATE_BROKER') ||
    can('CAN_CREATE_DEAL') ||
    can('CAN_EDIT_BROKER') ||
    can('CAN_DELETE_DEAL') ||
    can('CAN_EDIT_DEAL') ||
    can('CAN_MANAGE_PAYOUTS') ||
    can('CAN_MANAGE_COMMISSION_RATES') ||
    can('CAN_EDIT_SETTINGS') ||
    can('CAN_MANAGE_ACCOUNTS') ||
    can('CAN_RESET_PLATFORM');
  const { language, toggleLanguage } = useLanguage();
  const { clearSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isNarrow = useIsNarrow(SIDEBAR_BREAKPOINT_PX);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < SIDEBAR_BREAKPOINT_PX,
  );
  const [headerSearch, setHeaderSearch] = useState('');
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<AdminNotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Avatar / profile state (mirrors BrokerageLayout)
  const [headerProfile, setHeaderProfile] = useState<{ name: string; nameAr?: string; email: string; avatarUrl?: string | null } | null>(null);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const avatarMenuRef = useRef<HTMLDivElement>(null);
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null);
  const avatarBlobUrlRef = useRef<string | null>(null);
  const isRtl = language === 'ar';

  const refreshNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const { data, meta } = await getAdminNotifications();
      setNotifications(data);
      setUnreadCount(meta.unreadCount);
    } catch {
      // Non-blocking in header.
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(() => {
    getProfile()
      .then(({ data }) => {
        setHeaderProfile({ name: data.name, nameAr: data.nameAr, email: data.email, avatarUrl: data.avatarUrl ?? null });
        setAvatarVersion((v) => v + 1);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then(({ data }) => {
        if (!cancelled) setHeaderProfile({ name: data.name, email: data.email, avatarUrl: data.avatarUrl ?? null });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: refresh notifications on route change
    void refreshNotifications();
  }, [refreshNotifications, location.pathname]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void refreshNotifications();
    }, 30000);
    return () => window.clearInterval(id);
  }, [refreshNotifications]);

  useEffect(() => {
    if (!headerProfile?.avatarUrl) {
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear blob URL when avatar is removed
      setAvatarBlobUrl(null);
      return;
    }
    let cancelled = false;
    const avatarRequestUrl = headerProfile.avatarUrl?.startsWith('/') ? headerProfile.avatarUrl : '/api/profile/avatar';
    apiClient
      .get<Blob>(avatarRequestUrl, { responseType: 'blob' })
      .then((res) => {
        if (!cancelled && res.data) {
          if (avatarBlobUrlRef.current) URL.revokeObjectURL(avatarBlobUrlRef.current);
          const url = URL.createObjectURL(res.data);
          avatarBlobUrlRef.current = url;
          setAvatarBlobUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) setAvatarBlobUrl(null);
      });
    return () => { cancelled = true; };
  }, [headerProfile?.avatarUrl, avatarVersion]);

  useEffect(() => {
    return () => {
      if (avatarBlobUrlRef.current) {
        URL.revokeObjectURL(avatarBlobUrlRef.current);
        avatarBlobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: auto-collapse sidebar on narrow viewports
    if (isNarrow) setSidebarCollapsed(true);
  }, [isNarrow]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(e.target as Node)) {
        setQuickActionsOpen(false);
      }
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target as Node)) {
        setAvatarMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const isOverlay = isNarrow && !sidebarCollapsed;

  const visibleNavItems = ALL_NAV_ITEMS.filter((item) => {
    const required = NAV_PERMISSION_MAP[item.to];
    return required ? can(required) : true;
  });

  const handleSignOut = () => {
    clearSession();
    navigate(BACKOFFICE_LOGIN_PATH, { replace: true });
  };

  const handleNotificationClick = async (item: AdminNotificationItem) => {
    try {
      if (!item.isRead) {
        await markAdminNotificationRead(item.id);
      }
    } catch {
      // Keep navigation working even if mark-read fails.
    }
    setNotificationsOpen(false);
    void refreshNotifications();
    if (item.link) {
      navigate(item.link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAdminNotificationsRead();
      await refreshNotifications();
    } catch {
      addToast({
        variant: 'error',
        title: t('admin.header.notificationsMarkAllFailed', { defaultValue: 'Could not mark notifications as read.' }),
      });
    }
  };

  return (
    <div className="flex h-full overflow-hidden bg-surface-web font-sans" dir={isRtl ? 'rtl' : 'ltr'}>
      {isOverlay && (
        <div
          role="presentation"
          className="fixed inset-0 z-40 bg-black/20 transition-opacity duration-300 ease-in-out"
          onClick={() => setSidebarCollapsed(true)}
          onKeyDown={(e) => e.key === 'Escape' && setSidebarCollapsed(true)}
          aria-hidden
        />
      )}

      {/* Sidebar — same style as Saee (BrokerageLayout): border-e border-surface-border bg-surface-cards */}
      <motion.aside
        data-testid="admin-sidebar"
        className={`fixed start-0 top-0 z-50 flex h-screen flex-col overflow-hidden border-e border-surface-border bg-surface-cards ${isOverlay ? 'shadow-xl' : ''}`}
        initial={false}
        animate={{ width: sidebarWidth }}
        transition={springSidebar}
        aria-label={t('admin.nav.sidebarAriaLabel')}
        aria-expanded={!sidebarCollapsed}
      >
        <div className={`flex justify-center px-4 py-6 ${sidebarCollapsed ? 'px-2' : ''}`}>
          {sidebarCollapsed ? (
            <SaaeLogo variant="icon" mode="light" className="shrink-0" />
          ) : (
            <SaaeLogo size="small" mode="light" className="!h-[30.6px]" />
          )}
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-2 py-4">
          {visibleNavItems.map((item) => {
            const isActive =
              item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);
            const label = t(item.labelKey, { defaultValue: NAV_LABEL_DEFAULTS[item.labelKey] ?? item.labelKey });
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={label}
                className={({ isActive: active }) =>
                  `flex min-h-[48px] w-full items-center gap-2 rounded-[16px] py-2 text-start text-sm font-medium transition-colors font-sans ${
                    sidebarCollapsed ? 'justify-center px-0' : 'px-4'
                  } ${
                    active
                      ? 'bg-primary text-white'
                      : 'text-primary/70 hover:bg-surface-gray-lighter hover:text-primary'
                  }`
                }
                aria-current={isActive ? 'page' : undefined}
                aria-label={label}
              >
                <HugeiconsIcon
                  icon={item.icon}
                  size={20}
                  className={`shrink-0 ${isActive ? 'text-white' : 'text-primary/60'}`}
                />
                {!sidebarCollapsed && <span className="truncate">{label}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className={`px-2 pb-2 pt-0 ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
          <button
            type="button"
            onClick={() => setSidebarCollapsed((c) => !c)}
            className={`flex w-full items-center justify-center gap-2 py-2.5 text-secondary transition-all duration-300 ease-in-out focus:outline-none font-sans ${
              sidebarCollapsed
                ? 'rounded-[16px] border border-surface-border bg-surface-gray-lighter/50 px-0 hover:bg-secondary/20 hover:border-secondary/40 focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2'
                : 'rounded-[16px] border-0 bg-transparent px-4 hover:bg-secondary/20 focus-visible:ring-0'
            }`}
            aria-label={sidebarCollapsed ? t('dashboard.sidebarExpand') : t('dashboard.sidebarCollapse')}
          >
            <motion.span
              animate={{ rotate: sidebarCollapsed ? 180 : 0 }}
              transition={springSidebar}
              className="inline-flex shrink-0"
            >
              <RtlDirectionalIcon>
                <HugeiconsIcon icon={ArrowLeft01Icon} size={20} />
              </RtlDirectionalIcon>
            </motion.span>
            {!sidebarCollapsed && (
              <span className="text-sm font-medium">{t('dashboard.sidebarCollapse')}</span>
            )}
          </button>
        </div>
      </motion.aside>

      <motion.main
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-surface-web"
        style={{ marginInlineStart: isOverlay ? 0 : sidebarWidth }}
        initial={false}
        animate={{ marginInlineStart: isOverlay ? 0 : sidebarWidth }}
        transition={springSidebar}
      >
        {/* Header — same style as Saee (BrokerageLayout), with search field kept for admin */}
        <header className="sticky top-0 z-30 flex shrink-0 items-center justify-between gap-4 border-b border-surface-border bg-surface-cards ps-6 pe-6 py-4 sm:ps-8 sm:pe-8 font-sans">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="relative flex min-w-0 max-w-md flex-1">
              <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-primary/50" aria-hidden>
                <HugeiconsIcon icon={Search01Icon} size={20} />
              </span>
              <input
                type="search"
                placeholder={t('admin.header.searchPlaceholder', { defaultValue: 'بحث...' })}
                value={headerSearch}
                onChange={(e) => setHeaderSearch(e.target.value)}
                className="w-full min-w-0 rounded-[16px] border border-surface-gray-darker bg-surface-cards py-2.5 pe-3 ps-10 text-sm text-primary placeholder:text-primary/40 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 font-sans"
                aria-label={t('admin.header.searchPlaceholder', { defaultValue: 'بحث' })}
              />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-4 sm:gap-6">
            {!hasAnyWritePermission && (
              <span
                className="rounded-full border border-primary/25 bg-surface-primary-lighter px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary"
                title={t('admin.header.readOnlyHint', {
                  defaultValue: 'You can view data but cannot create, edit, or delete.',
                })}
              >
                {t('admin.header.readOnlyBadge', { defaultValue: 'Read-only' })}
              </span>
            )}
            <button
              type="button"
              onClick={toggleLanguage}
              className="flex items-center gap-2 rounded-[16px] border border-primary/30 px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-surface-primary-lighter font-sans"
              aria-label={t('common.language')}
              title={language === 'ar' ? t('common.english') : t('common.arabic')}
            >
              <HugeiconsIcon icon={GlobeIcon} size={20} className="shrink-0" />
              <span>{language === 'ar' ? t('common.english') : t('common.arabic')}</span>
            </button>
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                onClick={() => setNotificationsOpen((o) => !o)}
                className="relative rounded-[16px] p-2 text-primary/70 transition-colors hover:bg-surface-gray-lighter hover:text-primary font-sans"
                aria-label={t('admin.header.notifications', { defaultValue: 'Notifications' })}
                aria-expanded={notificationsOpen}
                aria-haspopup="true"
              >
                <HugeiconsIcon icon={Notification01Icon} size={24} />
                {unreadCount > 0 ? (
                  <span className="absolute -end-0.5 -top-0.5 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-error-primary px-1 text-[10px] font-semibold text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                ) : null}
              </button>
              <AnimatePresence>
                {notificationsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute end-0 top-full z-50 mt-2 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-[16px] border border-surface-border bg-surface-cards shadow-xl"
                  >
                    <div className="flex items-center justify-between border-b border-surface-border px-4 py-3">
                      <p className="text-sm font-semibold text-primary">
                        {t('admin.header.notifications', { defaultValue: 'Notifications' })}
                      </p>
                      <button
                        type="button"
                        onClick={() => void handleMarkAllRead()}
                        className="text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                        disabled={unreadCount === 0}
                      >
                        {t('admin.header.markAllRead', { defaultValue: 'Mark all as read' })}
                      </button>
                    </div>
                    <div className="max-h-96 overflow-auto">
                      {notificationsLoading ? (
                        <p className="px-4 py-6 text-center text-sm text-primary/60">
                          {t('common.loading', { defaultValue: 'Loading…' })}
                        </p>
                      ) : notifications.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-primary/60">
                          {t('admin.header.noNotifications', { defaultValue: 'No notifications yet.' })}
                        </p>
                      ) : (
                        notifications.map((item) => {
                          const displayTitle =
                            (item.title ?? '').trim() ||
                            t('admin.header.notificationFallbackTitle', { defaultValue: 'Notification' });
                          const displayMessage =
                            (item.message ?? '').trim() ||
                            t('admin.header.notificationFallbackBody', {
                              defaultValue: 'No details were provided for this notification.',
                            });
                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => void handleNotificationClick(item)}
                              className={`flex w-full items-start gap-3 border-b border-surface-border px-4 py-3 text-start transition-colors hover:bg-surface-gray-lighter/60 ${
                                item.isRead ? 'bg-surface-cards' : 'bg-primary/5'
                              }`}
                            >
                              <span className={`mt-1 inline-flex h-2.5 w-2.5 rounded-full ${item.isRead ? 'bg-surface-gray-darker' : 'bg-surface-error-primary'}`} />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold text-primary">{displayTitle}</span>
                                <span className="mt-0.5 block text-xs text-primary/70">{displayMessage}</span>
                              </span>
                              {!item.isRead ? (
                                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} className="mt-0.5 shrink-0 text-primary/50" />
                              ) : null}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {(can('CAN_CREATE_BROKER') || can('CAN_CREATE_DEAL')) && (
              <div className="relative" ref={quickActionsRef}>
                <button
                  type="button"
                  onClick={() => setQuickActionsOpen((o) => !o)}
                  className="flex items-center gap-2 rounded-[16px] border border-surface-border bg-surface-cards px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-surface-gray-lighter focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 font-sans"
                  aria-expanded={quickActionsOpen}
                  aria-haspopup="true"
                >
                  {t('admin.header.quickActions', { defaultValue: 'إجراءات سريعة' })}
                  <HugeiconsIcon icon={ArrowDown01Icon} size={18} className={`shrink-0 transition-transform ${quickActionsOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {quickActionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute end-0 top-full mt-1 min-w-[180px] rounded-[16px] border border-surface-border bg-surface-cards py-1 shadow-xl font-sans"
                      style={{ insetInlineEnd: 0 }}
                    >
                      {can('CAN_CREATE_BROKER') && (
                        <button
                          type="button"
                          onClick={() => {
                            setQuickActionsOpen(false);
                            navigate(backofficePath('brokers/new'));
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-start text-sm text-primary hover:bg-surface-gray-lighter font-sans"
                        >
                          <HugeiconsIcon icon={Add01Icon} size={18} />
                          {t('admin.header.addBroker', { defaultValue: 'إضافة وسيط' })}
                        </button>
                      )}
                      {can('CAN_CREATE_DEAL') && (
                        <button
                          type="button"
                          onClick={() => {
                            setQuickActionsOpen(false);
                            navigate(backofficePath('brokerage/new'));
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-start text-sm text-primary hover:bg-surface-gray-lighter font-sans"
                        >
                          <HugeiconsIcon icon={FileAddIcon} size={18} />
                          {t('admin.header.registerDeal', { defaultValue: 'تسجيل صفقة' })}
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* User avatar + dropdown (mirrors BrokerageLayout) */}
            <div className="relative flex items-center gap-3 sm:gap-4" ref={avatarMenuRef}>
              <button
                type="button"
                onClick={() => setAvatarMenuOpen((open) => !open)}
                className="flex items-center gap-3 sm:gap-4 rounded-[16px] p-1 pr-2 transition-colors hover:bg-surface-primary-lighter focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                aria-expanded={avatarMenuOpen}
                aria-haspopup="true"
                aria-label={t('dashboard.userProfile')}
              >
                <div className="aspect-square size-9 shrink-0 overflow-hidden rounded-full border border-surface-gray-darker bg-surface-primary-lighter sm:size-10">
                  <img
                    src={avatarBlobUrl ?? avatarDefault}
                    alt=""
                    width={40}
                    height={40}
                    className="size-full object-cover object-center"
                  />
                </div>
                <div className="flex flex-col items-start text-start min-w-0" dir={isRtl ? 'rtl' : 'ltr'}>
                  <span className="text-sm font-medium text-primary font-sans truncate" title={(isRtl ? (headerProfile?.nameAr || headerProfile?.name) : headerProfile?.name) ?? undefined}>
                    {(isRtl ? (headerProfile?.nameAr || headerProfile?.name) : headerProfile?.name) ?? '…'}
                  </span>
                  <span className="text-sm text-primary/60 truncate" title={headerProfile?.email ?? undefined}>
                    {headerProfile?.email || '…'}
                  </span>
                </div>
              </button>
              {avatarMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="absolute end-0 top-full z-50 mt-2 min-w-[12rem] rounded-[16px] border border-surface-border bg-surface-cards py-2 shadow-lg"
                  role="menu"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-start text-sm font-medium text-primary font-sans transition-colors hover:bg-surface-primary-lighter"
                    onClick={() => {
                      setAvatarMenuOpen(false);
                      setAvatarModalOpen(true);
                    }}
                  >
                    <HugeiconsIcon icon={Camera01Icon} size={20} className="shrink-0 text-primary/70" />
                    {t('dashboard.changeAvatar', { defaultValue: 'Change avatar' })}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-start text-sm font-medium text-primary font-sans transition-colors hover:bg-surface-primary-lighter"
                    onClick={() => {
                      setAvatarMenuOpen(false);
                      handleSignOut();
                    }}
                  >
                    <HugeiconsIcon icon={Logout01Icon} size={20} className="shrink-0 text-primary/70" />
                    {t('dashboard.signOut', { defaultValue: 'Sign out' })}
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </header>

        {avatarModalOpen && (
          <Suspense fallback={null}>
            <AvatarUploadModal
              open={avatarModalOpen}
              onClose={() => setAvatarModalOpen(false)}
              onSuccess={refreshProfile}
            />
          </Suspense>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-6 sm:p-8">
          <motion.div
            key={location.pathname}
            initial={pageTransition.initial}
            animate={pageTransition.animate}
            transition={pageTransition.transition}
            className="min-h-0"
          >
            <Outlet />
          </motion.div>
        </div>
      </motion.main>
    </div>
  );
}
